<template>
  <a-table
    rowKey="id"
    :scroll="{ x: 'max-content' }"
    :columns="columns"
    :data-source="movements"
    :pagination="{
      current: pagination.page,
      pageSize: pagination.size,
      total: pagination.count
    }"
    :loading="loading.fetchIntegrationMovements"
    @change="onTableChange"
  >
    <template #date="{ text }">
      <span>
        {{ new Date(text).toLocaleDateString('es-CL', { timeZone: 'UTC' }) }}
      </span>
    </template>

    <template #datetime="{ text }">
      <span>
        {{
          new Date(text).toLocaleDateString('es-CL', {
            timeZone: 'America/Santiago'
          })
        }}
      </span>
    </template>

    <template #textOrDash="{ text }">
      <span>{{ displayValue(text) }}</span>
    </template>

    <template #stateTag="{ record }">
      <IntegrationMovementStateTag
        :state-id="record.stateId"
        :state-tag="record.stateTag"
      />
    </template>

    <template #typeTag="{ record }">
      <IntegrationMovementTypeTag
        :type-id="record.typeId"
        :type-tag="record.typeTag"
      />
    </template>

    <template #action="{ record }">
      <a-button
        type="link"
        size="small"
        @click="$emit('viewMovement', record.id)"
      >
        <template #icon>
          <EyeOutlined />
        </template>
      </a-button>
    </template>

    <template #searchIcon="{ filtered }">
      <SearchOutlined :style="{ color: filtered ? 'primary' : undefined }" />
    </template>

    <template #calendarIcon="{ filtered }">
      <CalendarOutlined :style="{ color: filtered ? 'primary' : undefined }" />
    </template>

    <template
      #textSearch="{ setSelectedKeys, selectedKeys, confirm, clearFilters }"
    >
      <TextSearch
        :set-selected-keys="setSelectedKeys"
        :selected-keys="selectedKeys"
        :confirm="confirm"
        :clear-filters="clearFilters"
      />
    </template>

    <template
      #rutSearch="{ setSelectedKeys, selectedKeys, confirm, clearFilters }"
    >
      <RutSearch
        :set-selected-keys="setSelectedKeys"
        :selected-keys="selectedKeys"
        :confirm="confirm"
        :clear-filters="clearFilters"
      />
    </template>

    <template
      #dateRangeSearch="{
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters
      }"
    >
      <DateRangeSearch
        :set-selected-keys="setSelectedKeys"
        :selected-keys="selectedKeys"
        :confirm="confirm"
        :clear-filters="clearFilters"
      />
    </template>

    <template
      #typeSearch="{ setSelectedKeys, selectedKeys, confirm, clearFilters }"
    >
      <SelectSearch
        :set-selected-keys="setSelectedKeys"
        :selected-keys="selectedKeys"
        :confirm="confirm"
        :clear-filters="clearFilters"
        :options="typeOptions"
        :loading="loading.fetchIntegrationMovementTypes"
      />
    </template>

    <template
      #companiesSearch="{
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters
      }"
    >
      <SelectSearch
        :set-selected-keys="setSelectedKeys"
        :selected-keys="selectedKeys"
        :confirm="confirm"
        :clear-filters="clearFilters"
        :options="companyOptions"
        :loading="loadingCompanies"
        mode="multiple"
      />
    </template>

    <template
      #stateSearch="{ setSelectedKeys, selectedKeys, confirm, clearFilters }"
    >
      <SelectSearch
        :set-selected-keys="setSelectedKeys"
        :selected-keys="selectedKeys"
        :confirm="confirm"
        :clear-filters="clearFilters"
        :options="stateOptions"
        :loading="loading.fetchIntegrationMovementStates"
        mode="multiple"
      />
    </template>

    <template
      #brokerSearch="{ setSelectedKeys, selectedKeys, confirm, clearFilters }"
    >
      <SelectSearch
        :set-selected-keys="setSelectedKeys"
        :selected-keys="selectedKeys"
        :confirm="confirm"
        :clear-filters="clearFilters"
        :options="brokerOptions"
      />
    </template>
  </a-table>
</template>

<script setup>
import {
  INTEGRATION_MOVEMENTS_GET_INTEGRATION_MOVEMENTS,
  INTEGRATION_MOVEMENTS_FETCH_INTEGRATION_MOVEMENTS,
  INTEGRATION_MOVEMENTS_GET_LOADING,
  INTEGRATION_MOVEMENTS_GET_PAGINATION,
  INTEGRATION_MOVEMENTS_GET_INTEGRATION_MOVEMENT_TYPES,
  COMPANIES_GET_LOADING,
  COMPANIES_GET_COMPANIES,
  INTEGRATION_MOVEMENTS_GET_INTEGRATION_MOVEMENT_STATES,
  GET_ALL_BROKERS
} from '@/store/types';
import {
  CalendarOutlined,
  EyeOutlined,
  SearchOutlined
} from '@ant-design/icons-vue';
import { computed } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import moment from 'moment';
import IntegrationMovementStateTag from '@/new-components/integration/movements/integration-movement-state-tag.vue';
import IntegrationMovementTypeTag from '@/new-components/integration/movements/integration-movement-type-tag.vue';
import TextSearch from '@/bewell-components/dropdowns/text-search.vue';
import RutSearch from '@/bewell-components/dropdowns/rut-search.vue';
import DateRangeSearch from '@/bewell-components/dropdowns/date-range-search.vue';
import SelectSearch from '@/bewell-components/dropdowns/select-search.vue';

const store = useStore();
const router = useRouter();
const route = useRoute();

const typeOptions = computed(() =>
  store.getters[INTEGRATION_MOVEMENTS_GET_INTEGRATION_MOVEMENT_TYPES].map(
    (type) => ({
      label: type.tag,
      value: type.id
    })
  )
);
const companyOptions = computed(() =>
  store.getters[COMPANIES_GET_COMPANIES].map((company) => ({
    label: company.businessName,
    value: company.id
  }))
);
const stateOptions = computed(() =>
  store.getters[INTEGRATION_MOVEMENTS_GET_INTEGRATION_MOVEMENT_STATES].map(
    (state) => ({
      label: state.tag,
      value: state.id
    })
  )
);
const brokerOptions = computed(() =>
  store.getters[GET_ALL_BROKERS].map((broker) => ({
    label: broker.name,
    value: broker.id
  }))
);
const movements = computed(
  () => store.getters[INTEGRATION_MOVEMENTS_GET_INTEGRATION_MOVEMENTS]
);
const pagination = computed(
  () => store.getters[INTEGRATION_MOVEMENTS_GET_PAGINATION]
);
const loading = computed(
  () => store.getters[INTEGRATION_MOVEMENTS_GET_LOADING]
);
const loadingCompanies = computed(
  () => store.getters[COMPANIES_GET_LOADING].fetchCompanies
);

const displayValue = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  return value;
};

const onTableChange = (pagination, filters) => {
  const { current, pageSize } = pagination;
  const query = {
    ...route.query,
    movementTypeId: filters.typeTag ? filters.typeTag[0] : undefined,
    holdingName: filters.holdingName ? filters.holdingName[0] : undefined,
    subholdingName: filters.subholdingName
      ? filters.subholdingName[0]
      : undefined,
    companyIds: filters.companyName[0],
    rut: filters.rut ? filters.rut[0] : undefined,
    fullname: filters.fullname ? filters.fullname[0] : undefined,
    movementStartDate: filters.movementDate[0]
      ? filters.movementDate[0].format('YYYY-MM-DD')
      : undefined,
    movementEndDate: filters.movementDate[1]
      ? filters.movementDate[1].format('YYYY-MM-DD')
      : undefined,
    createdStartDate: filters.createdAt[0]
      ? filters.createdAt[0].format('YYYY-MM-DD')
      : undefined,
    createdEndDate: filters.createdAt[1]
      ? filters.createdAt[1].format('YYYY-MM-DD')
      : undefined,
    stateIds: filters.stateTag[0],
    brokerId: filters.brokerName ? filters.brokerName[0] : undefined,
    page: current,
    size: pageSize
  };
  router.replace({
    query
  });
  store.dispatch(INTEGRATION_MOVEMENTS_FETCH_INTEGRATION_MOVEMENTS, {
    ...query
  });
};

const columns = computed(() => {
  const movementDateFilter = [];
  if (route.query.movementStartDate) {
    movementDateFilter.push(
      moment(route.query.movementStartDate, 'YYYY-MM-DD')
    );
  }
  if (route.query.movementEndDate) {
    movementDateFilter.push(moment(route.query.movementEndDate, 'YYYY-MM-DD'));
  }

  const createdDateFilter = [];
  if (route.query.createdStartDate) {
    createdDateFilter.push(moment(route.query.createdStartDate, 'YYYY-MM-DD'));
  }
  if (route.query.createdEndDate) {
    createdDateFilter.push(moment(route.query.createdEndDate, 'YYYY-MM-DD'));
  }

  if (Array.isArray(route.query.companyIds)) {
    route.query.companyIds = route.query.companyIds.map((id) => Number(id));
  } else if (route.query.companyIds) {
    route.query.companyIds = [Number(route.query.companyIds)];
  }

  if (Array.isArray(route.query.stateIds)) {
    route.query.stateIds = route.query.stateIds.map((id) => Number(id));
  } else if (route.query.stateIds) {
    route.query.stateIds = [Number(route.query.stateIds)];
  }

  return [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id'
    },
    {
      title: 'Tipo',
      dataIndex: 'typeTag',
      key: 'typeTag',
      slots: {
        customRender: 'typeTag',
        filterDropdown: 'typeSearch'
      },
      filteredValue: route.query.movementTypeId
        ? [Number(route.query.movementTypeId)]
        : []
    },
    {
      title: 'Holding',
      dataIndex: 'holdingName',
      key: 'holdingName',
      filteredValue: route.query.holdingName ? [route.query.holdingName] : [],
      slots: {
        filterDropdown: 'textSearch',
        filterIcon: 'searchIcon'
      }
    },
    {
      title: 'Subholding',
      dataIndex: 'subholdingName',
      key: 'subholdingName',
      filteredValue: route.query.subholdingName
        ? [route.query.subholdingName]
        : [],
      slots: {
        filterDropdown: 'textSearch',
        filterIcon: 'searchIcon'
      }
    },
    {
      title: 'Empresa',
      dataIndex: 'companyName',
      key: 'companyName',
      filteredValue: route.query.companyIds ? [route.query.companyIds] : [],
      slots: {
        filterDropdown: 'companiesSearch'
      }
    },
    {
      title: 'RUT',
      dataIndex: 'rut',
      key: 'rut',
      filteredValue: route.query.rut ? [route.query.rut] : [],
      slots: {
        filterDropdown: 'rutSearch',
        filterIcon: 'searchIcon'
      }
    },
    {
      title: 'Nombre',
      dataIndex: 'fullname',
      key: 'fullname',
      filteredValue: route.query.fullname ? [route.query.fullname] : [],
      slots: {
        filterDropdown: 'textSearch',
        filterIcon: 'searchIcon'
      }
    },
    {
      title: 'Tipo contrato',
      dataIndex: 'contractType',
      key: 'contractType',
      slots: {
        customRender: 'textOrDash'
      }
    },
    {
      title: 'Fecha de movimiento',
      dataIndex: 'movementDate',
      key: 'movementDate',
      filteredValue: movementDateFilter,
      slots: {
        customRender: 'date',
        filterDropdown: 'dateRangeSearch',
        filterIcon: 'calendarIcon'
      }
    },
    {
      title: 'Fecha de importación',
      dataIndex: 'createdAt',
      key: 'createdAt',
      filteredValue: createdDateFilter,
      slots: {
        customRender: 'datetime',
        filterDropdown: 'dateRangeSearch',
        filterIcon: 'calendarIcon'
      }
    },
    {
      title: 'Estado',
      dataIndex: 'stateTag',
      key: 'stateTag',
      filteredValue: route.query.stateIds ? [route.query.stateIds] : [],
      slots: {
        customRender: 'stateTag',
        filterDropdown: 'stateSearch'
      }
    },
    {
      title: 'Ejecutivo',
      dataIndex: 'brokerName',
      key: 'brokerName',
      filteredValue: route.query.brokerId ? [Number(route.query.brokerId)] : [],
      slots: {
        filterDropdown: 'brokerSearch',
        filterIcon: 'searchIcon'
      }
    },
    {
      title: 'Fuente',
      dataIndex: 'source',
      key: 'source'
    },
    {
      width: 95,
      fixed: 'right',
      align: 'center',
      title: 'Acción',
      dataIndex: 'id',
      key: 'id',
      slots: {
        customRender: 'action'
      }
    }
  ];
});
</script>
